import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { snakeCase } from 'lodash'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isFailed, isPending, isSuccess } from 'redux/toolkit/api'
import {
  deleteScheduledReport,
  getScheduledReportList,
  resetScheduledReportsList,
  resetScheduledReportUpdate,
  setSelectedListItem,
  updateScheduledReport
} from 'redux/features/reports/reportsSlice'
import { BarracudaReportTypes, ParsedScheduleReport, RelativeDateRanges, ScheduleReportFormat } from 'types/reports'
import { formatDate } from 'lib/datetime'
import config from 'config/appConfig'
import { useFormatMessage } from 'lib/localization'
import routesConfig from 'lib/routesConfig'
import { TRACKING_EVENTS, trackMixpanelEvent } from 'lib/monitoring/monitoringService'

export interface State {
  isLoading: boolean
  isGetScheduledReportsListFailed: boolean
  scheduledReports: ParsedScheduleReport[]
  search: string
  isScheduleReportOpen: boolean
  editItem?: ParsedScheduleReport
  anchorEl: HTMLButtonElement | null
  menuItems: {
    label: string
    onClick: () => void
  }[]
}

export interface EventHandlers {
  onSearch: Dispatch<SetStateAction<string>>
  onSchedule: (item?: ParsedScheduleReport) => void
  onCloseSchedule: () => void
  onOpenDotsMenu: (event: React.MouseEvent<HTMLButtonElement>, selectedReport: ParsedScheduleReport) => void
  onCloseDotsMenu: () => void
}

export enum MENU_ITEMS {
  enableSchedule = 'enable_schedule',
  disableSchedule = 'disable_schedule',
  deleteSchedule = 'delete_schedule',
  openReport = 'open_report'
}

const BASE_I18N_KEY = 'ess.reports.scheduled_reports'
const BASE_I18N_REPORTS_KEY = 'ess.reports'

export const useScheduledReportsLogic = (): [State, EventHandlers] => {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const reportFormatMessage = useFormatMessage(BASE_I18N_REPORTS_KEY)
  const dispatch = useAppDispatch()
  const {
    isGetScheduledReportsListInProgress,
    isGetScheduledReportsListIsFailed,
    isScheduleReportSuccess,
    isUpdateScheduledReportSuccess,
    isUpdateScheduledReportPending,
    isDeleteScheduledReportSuccess,
    isDeleteScheduledReportPending,
    scheduledReports,
    selectedListItem
  } = useAppSelector(_store => ({
    isGetScheduledReportsListInProgress: isPending(_store.reports.api.getScheduledReportsListApiStatus),
    isGetScheduledReportsListIsFailed: isFailed(_store.reports.api.getScheduledReportsListApiStatus),
    isScheduleReportSuccess: isSuccess(_store.reports.api.scheduleReportApiStatus),
    isUpdateScheduledReportSuccess: isSuccess(_store.reports.api.updateScheduledReportApiStatus),
    isUpdateScheduledReportPending: isPending(_store.reports.api.updateScheduledReportApiStatus),
    isDeleteScheduledReportSuccess: isSuccess(_store.reports.api.deleteScheduledReportApiStatus),
    isDeleteScheduledReportPending: isPending(_store.reports.api.deleteScheduledReportApiStatus),
    scheduledReports: _store.reports.scheduledList,
    selectedListItem: _store.reports.selectedListItem
  }))
  const [search, setSearch] = useState<string>('')
  const [isScheduleReportOpen, setIsScheduleReportOpen] = useState(false)
  const [editItem, setEditItem] = useState<ParsedScheduleReport | undefined>(undefined)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [menuItems, setMenuItems] = useState<any>([])

  // init
  useEffect(() => {
    trackMixpanelEvent(TRACKING_EVENTS.WEBUI.SCHEDULE_REPORTS_PAGE_VIEW)
    dispatch(getScheduledReportList())

    return () => {
      dispatch(resetScheduledReportsList())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const scheduledReportsFiltered: ParsedScheduleReport[] = useMemo(
    () =>
      scheduledReports.reduce((perv, current) => {
        if (!current.reportId || !current.reportId.toLowerCase().includes(search.toLowerCase())) {
          return perv
        }

        const transfomredRelativeDateRange = (snakeCase(current.relativeDateRange) as RelativeDateRanges) || ''

        const mappedFrequency = current.scheduledFrequency.toLowerCase()

        const formattedDate = formatDate(new Date(current.nextRunIso), config.DATETIME.DEFAULT_DATE_FORMAT)
        const formattedHours = formatDate(new Date(current.nextRunIso), config.DATETIME.REPORT_TIME_FORMAT)
        const formattedLastDate = current.lastRunIso
          ? formatDate(new Date(current.lastRunIso), config.DATETIME.DEFAULT_DATE_FORMAT)
          : null
        const formattedLastHours = current.lastRunIso
          ? formatDate(new Date(current.lastRunIso), config.DATETIME.REPORT_TIME_FORMAT)
          : null

        let formattedRecipients = current.recipients
        if (formattedRecipients.length > 11) {
          const hiddenNumber = formattedRecipients.length - 10
          formattedRecipients = [...formattedRecipients.slice(0, 10), `+ ${hiddenNumber} ${formatMessage('more')}`]
        }

        perv.push({
          ...current,
          format: ScheduleReportFormat.csv,
          relativeDateRange: transfomredRelativeDateRange,
          scheduledFrequency: mappedFrequency,
          at: formattedHours,
          formattedDate,
          formattedHours,
          formattedLastDate,
          formattedLastHours,
          formattedRecipients
        })

        return perv
      }, [] as ParsedScheduleReport[]),
    [search, scheduledReports, formatMessage]
  )

  const onSchedule = useCallback((item?: ParsedScheduleReport) => {
    setIsScheduleReportOpen(true)
    if (item) {
      setEditItem(item)
    } else {
      setEditItem(undefined)
    }
  }, [])

  const onCloseSchedule = useCallback(() => {
    setIsScheduleReportOpen(false)
  }, [])

  const handleOpenReport = useCallback(
    (selectedReport: ParsedScheduleReport) => {
      dispatch(
        setSelectedListItem({
          id: selectedReport.id,
          type: selectedReport.reportId as BarracudaReportTypes,
          label: reportFormatMessage(`predefined_report_types.${selectedReport.reportId}`)
        })
      )
      routesConfig.REPORT_LIST.goto()
    },
    [dispatch, reportFormatMessage]
  )

  const onOpenDotsMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, selectedReport: ParsedScheduleReport) => {
      setAnchorEl(event.currentTarget)
      setMenuItems([
        {
          label: !selectedReport.enabled ? MENU_ITEMS.enableSchedule : MENU_ITEMS.disableSchedule,
          onClick: () => {
            dispatch(
              updateScheduledReport({
                id: [selectedReport.id],
                enabled: !selectedReport.enabled
              })
            )
            setAnchorEl(null)
          }
        },
        {
          label: MENU_ITEMS.deleteSchedule,
          onClick: () => {
            dispatch(deleteScheduledReport({ report_id: selectedReport.id }))
            setAnchorEl(null)
          }
        },
        {
          label: MENU_ITEMS.openReport,
          onClick: () => {
            handleOpenReport(selectedReport)
            setAnchorEl(null)
          }
        }
      ])
    },
    [dispatch, handleOpenReport]
  )

  const onCloseDotsMenu = useCallback(() => setAnchorEl(null), [])

  useEffect(() => {
    if (isUpdateScheduledReportSuccess || isDeleteScheduledReportSuccess || isScheduleReportSuccess) {
      dispatch(resetScheduledReportUpdate())
      dispatch(getScheduledReportList())
    }
  }, [dispatch, isUpdateScheduledReportSuccess, isDeleteScheduledReportSuccess, isScheduleReportSuccess])

  return useMemo(
    () => [
      {
        isLoading:
          isGetScheduledReportsListInProgress || isUpdateScheduledReportPending || isDeleteScheduledReportPending,
        isGetScheduledReportsListFailed: isGetScheduledReportsListIsFailed,
        scheduledReports: scheduledReportsFiltered,
        search,
        isScheduleReportOpen,
        editItem,
        anchorEl,
        menuItems
      },
      {
        onSearch: setSearch,
        onSchedule,
        onCloseSchedule,
        onOpenDotsMenu,
        onCloseDotsMenu
      }
    ],
    [
      isGetScheduledReportsListInProgress,
      isGetScheduledReportsListIsFailed,
      isUpdateScheduledReportPending,
      isDeleteScheduledReportPending,
      scheduledReportsFiltered,
      search,
      isScheduleReportOpen,
      editItem,
      anchorEl,
      menuItems,
      setSearch,
      onSchedule,
      onCloseSchedule,
      onOpenDotsMenu,
      onCloseDotsMenu
    ]
  )
}
